import { Injectable } from "@angular/core";
import { Subscription } from "rxjs";
import { environment } from "@apps/patient-core/src/app/environments/environment";
import Bugsnag from "@bugsnag/js";
import { WindowService } from "@apps/patient-core/src/app/services/window.service";
import { BaseAuthService } from "@apps/patient-core/src/app/services/abstracts/auth.service";
import { themeColour } from "../utils/theme";
import { isProduction } from "@shared/utils";

const { host, protocol } = window.location;

export interface I_ManifestShortcut {
  name: string;
  url: string;
  description: string;
  icons: Array<I_ManifestIcon>;
}

export interface I_ManifestScreenshot {
  src: string;
  type: string;
  sizes: string;
  form_factor: string;
  label: string;
}

export interface I_Manifest {
  name: string;
  short_name: string;
  description: string;
  categories: Array<string>;
  start_url: string;
  scope: string;
  background_color: string;
  theme_color: string;
  display?: string;
  orientation: string;
  icons: Array<I_ManifestIcon>;
  screenshots: Array<I_ManifestScreenshot>;
  shortcuts: Array<I_ManifestShortcut>;
}

const DEFAULT_FAVICON_PATH = `${protocol}//${host}/assets/`;
const PWA_SCREENSHOTS_PATH = `${protocol}//${host}/assets/screenshots/`;
export const PWA_SHORTCUTS_PATH = `${protocol}//${host}/assets/shortcuts/`;

export interface I_ManifestIcon {
  src: string;
  type: string;
  sizes: string;
}

@Injectable({
  providedIn: "root",
})
export class PwaService {
  private _timerSub: Subscription;
  constructor(private _authService: BaseAuthService, private _windowService: WindowService) {}

  public get isPWA(): boolean {
    if ("standalone" in this._windowService.navigator) {
      return this._windowService.navigator.standalone === true;
    }

    const mediaQuery = this._windowService.matchMedia("(display-mode: standalone)");

    return !!mediaQuery?.matches;
  }

  public setupPwa(): void {
    this._setupManifest();
    this._setupFavIcons();
  }

  private _setupManifest(): void {
    const manifestEl = document.getElementById("web-manifest");
    const manifest = this._generateManifest();

    if (manifestEl) manifestEl.setAttribute("href", manifest);
  }

  public async unregisterServiceWorker(): Promise<void> {
    try {
      // Check for the angular service worker and unregister it
      if (window.navigator && navigator.serviceWorker) {
        const registrations = await navigator.serviceWorker.getRegistrations();
        for (const registration of registrations) {
          const isAngularServiceWorker = registration.active?.scriptURL.includes("ngsw-worker.js");
          if (isAngularServiceWorker) await registration.unregister();
        }
      }
    } catch (error) {
      Bugsnag.notify("Error unregistering service worker", error);
    }
  }

  private _setupFavIcons(): void {
    const hasIcons = document.querySelector("link[rel='icon']") !== null;
    if (hasIcons) return;

    const iconHtml = `<link rel="icon" type="image/x-icon" href="${DEFAULT_FAVICON_PATH}favicon.ico" />`;

    // Add the icons to the page
    const headElement = document.querySelector("head");

    if (headElement) {
      headElement.insertAdjacentHTML("beforeend", iconHtml);
    }
  }

  private _generateManifest(): string {
    const isLoggedIn = this._authService.isLoggedIn();
    const prefix = isLoggedIn ? "/my-dental" : "";
    const start_url = `${window.location.origin}${prefix}`;

    const { STAGE } = environment;

    const name = isProduction(STAGE) ? `Dentally Portal` : `Dentally Portal (${STAGE})`;

    const manifest: I_Manifest = {
      name,
      short_name: name,
      description: "Your complete digital in practice assistant. Streamline check-ins, forms and treatment plans with ease.",
      start_url,
      categories: ["health", "medical", "productivity"],
      scope: start_url,
      background_color: "#FFFFFF",
      theme_color: themeColour,
      orientation: "any", //set to any to use device orientation on Android, Safari iOS doesn't support manifest orientation
      icons: this._generateManifestIcons(),
      screenshots: this._generateScreenshots(),
      shortcuts: this._generatePatientShortcuts(start_url),
    };

    // Adding a display property makes the PWA installable and passes the audit (https://developer.chrome.com/docs/lighthouse/pwa/installable-manifest)
    if (environment.PWA_INSTALLABLE === "true") manifest.display = "standalone";

    const content = encodeURIComponent(JSON.stringify(manifest));
    const url = "data:application/manifest+json," + content;

    return url;
  }

  private _generateScreenshots(): Array<I_ManifestScreenshot> {
    const screenshots = new Array<I_ManifestScreenshot>();
    const sizes: Record<string, string> = {
      narrow: "320x640",
      wide: "640x480",
    };

    const files: Record<string, string> = {
      start: "Patient check-in",
      medical: "Filling in a medical history form ahead of an appointment",
      complete: "Ready for an appointment",
    };

    for (const size of Object.keys(sizes)) {
      for (const [key, value] of Object.entries(files)) {
        const src = `${PWA_SCREENSHOTS_PATH}${size}/${key}.png`;
        screenshots.push({
          src,
          type: "image/png",
          sizes: sizes[size],
          form_factor: size,
          label: value,
        });
      }
    }

    return screenshots;
  }

  private _generatePatientShortcuts(start_url: string): Array<I_ManifestShortcut> {
    return [
      {
        name: "Send NHS PR Form",
        url: `${start_url}/login/action/NHS_PR`,
        description: "Send NHS PR form to the patient",
        icons: [
          {
            src: `${PWA_SHORTCUTS_PATH}action.png`,
            type: "image/png",
            sizes: "96x96",
          },
        ],
      },
    ];
  }

  private _generateManifestIcons(): Array<I_ManifestIcon> {
    const icons: Array<I_ManifestIcon> = [
      {
        src: `${DEFAULT_FAVICON_PATH}favicon.ico`,
        type: "image/x-icon",
        sizes: "16x16",
      },
      {
        src: `${DEFAULT_FAVICON_PATH}favicon_144x144.png`,
        type: "image/png",
        sizes: "144x144",
      },
    ];

    return icons;
  }
}
